// ============================================================================
// player.js — Commander Benjen Daguth. Runs at the fixed logic rate. Owns the
// combo state machine (reads combos.js), dodge i-frames, blocking, jump/aerial
// attacks, Titanfall trigger and taking damage. Movement is camera-relative
// (input.js resolves WASD against camera yaw into world-space moveX/moveZ).
// ============================================================================

import {
  PLAYER_SPEED, SPRINT_MULT, PLAYER_ACCEL, PLAYER_TURN_RATE, DODGE_SPEED, DODGE_TIME,
  DODGE_IFRAME_START, DODGE_IFRAME_END, DODGE_COOLDOWN, JUMP_V, GRAVITY,
  MAP_HALF, BLOCK_ARC, BLOCK_CHIP, TITAN_GAIN_TAKE, GUARDBREAK_STUN,
  TEAM_PLAYER, ST_DEAD, SHAKE_MAX,
} from '../core/constants.js';
import {
  fwdX, fwdZ, clamp, clamp01, angleDamp, headingTo, angleDelta, easeInCubic, dist2,
} from '../core/math.js';
import { NODES, START_NORMAL, START_CHARGE } from '../combat/combos.js';
import { resolveHit } from './hits.js';
import { spawnShockwave, addTitan, startTitanfall, canTitanfall } from './titan.js';
import { EV } from '../core/events.js';

const _win = [false, false, false, false];
const _tok = [0, 0, 0, 0];

function nearestEnemy(world, x, z, maxR) {
  let best = null, bestD = maxR * maxR;
  world.hash.forEachInRadius(x, z, maxR, (slot) => {
    const u = world.units.get(slot);
    if (!u._active || u.team === TEAM_PLAYER || u.state === ST_DEAD) return;
    const d = dist2(x, z, u.x, u.z);
    if (d < bestD) { bestD = d; best = u; }
  });
  return best;
}

function startAttack(world, node, aim) {
  const p = world.player;
  p.state = 'attack';
  p.atkNode = node;
  p.attackT = 0;
  p._winOpen = _win; p._winOpen[0] = p._winOpen[1] = p._winOpen[2] = p._winOpen[3] = false;
  p._shockDone = false;
  p.buffer = null; p.bufferT = 0;
  // soft aim: face the intended direction, or snap toward a nearby foe
  if (aim != null) {
    p.heading = aim;
  } else {
    const foe = nearestEnemy(world, p.x, p.z, 5.5);
    if (foe) p.heading = headingTo(p.x, p.z, foe.x, foe.z);
  }
  world.events.emit(EV.SWING, { heavy: !!(node.hits[0] && node.hits[0].heavy) });
}

function startDodge(world, dirX, dirZ) {
  const p = world.player;
  p.state = 'dodge';
  p.dodgeT = 0;
  p.dodgeCd = DODGE_COOLDOWN + DODGE_TIME;
  const m = Math.hypot(dirX, dirZ);
  if (m > 0.001) { p.dodgeDirX = dirX / m; p.dodgeDirZ = dirZ / m; }
  else { p.dodgeDirX = fwdX(p.heading); p.dodgeDirZ = fwdZ(p.heading); }
  p.heading = Math.atan2(p.dodgeDirX, p.dodgeDirZ);
  world._dust = true; // renderer picks up a dust puff
}

function groundMove(world, dt, speed, turn) {
  const p = world.player;
  const want = Math.hypot(p.moveX, p.moveZ);
  const tvx = want > 0.001 ? (p.moveX / want) * speed * Math.min(1, want) : 0;
  const tvz = want > 0.001 ? (p.moveZ / want) * speed * Math.min(1, want) : 0;
  // accelerate toward target velocity
  p.vx += clamp(tvx - p.vx, -PLAYER_ACCEL * dt, PLAYER_ACCEL * dt);
  p.vz += clamp(tvz - p.vz, -PLAYER_ACCEL * dt, PLAYER_ACCEL * dt);
  p.x += p.vx * dt; p.z += p.vz * dt;
  if (want > 0.05) {
    const h = Math.atan2(p.moveX, p.moveZ);
    p.heading = angleDamp(p.heading, h, turn, dt);
  }
  return want;
}

export function updatePlayer(world, input, dt) {
  const p = world.player;
  if (p.state === 'dead') return;

  // pull this frame's camera-relative movement intent onto the player so all
  // the movement/aim code below (groundMove, dodge dir, attack aim) can read it.
  p.moveX = input.moveX;
  p.moveZ = input.moveZ;
  p.sprinting = false;   // set true only while actually sprint-running below

  if (p.invuln > 0) p.invuln -= dt;
  if (p.dodgeCd > 0) p.dodgeCd -= dt;

  // ---- guard-broken / hurt lock ----
  if (p.guardBroken > 0) {
    p.guardBroken -= dt;
    p.vx *= 0.85; p.vz *= 0.85;
    p.x += p.vx * dt; p.z += p.vz * dt;
    if (p.guardBroken <= 0) p.state = 'idle';
    clampToField(p);
    world.colliders.resolve(p, p.radius);   // resolve LAST — never end a tick inside a solid
    return;
  }

  switch (p.state) {
    case 'idle':
    case 'run': {
      // Titanfall
      if (input.takeTitan() && canTitanfall(world)) { startTitanfall(world); break; }
      // block (held)
      if (input.blockHeld && p.y <= 0) { p.state = 'block'; p.blocking = true; break; }
      // dodge / jump on the same key: rolling if moving, hop if planted.
      // Both require being grounded — no air-rolls or double-jumps.
      if (input.takeDodge() && p.dodgeCd <= 0 && p.y <= 0) {
        if (Math.hypot(p.moveX, p.moveZ) > 0.2) startDodge(world, p.moveX, p.moveZ);
        else { p.vy = JUMP_V; p.y += p.vy * dt; }
        break;
      }
      // attacks
      const aim = (Math.hypot(p.moveX, p.moveZ) > 0.2) ? Math.atan2(p.moveX, p.moveZ) : null;
      if (p.y > 0.05) {
        if (input.takeN() || input.takeC()) { startAttack(world, NODES.JUMP, aim); break; }
      } else {
        if (input.takeN()) { startAttack(world, NODES[START_NORMAL], aim); break; }
        if (input.takeC()) { startAttack(world, NODES[START_CHARGE], aim); break; }
      }
      const sprinting = input.sprint && p.y <= 0 && Math.hypot(p.moveX, p.moveZ) > 0.3;
      p.sprinting = sprinting;
      const mag = groundMove(world, dt, sprinting ? PLAYER_SPEED * SPRINT_MULT : PLAYER_SPEED, PLAYER_TURN_RATE);
      p.state = mag > 0.08 ? 'run' : 'idle';
      break;
    }

    case 'attack':
      updateAttack(world, input, dt);
      break;

    case 'dodge':
      updateDodge(world, input, dt);
      break;

    case 'block': {
      if (!input.blockHeld) { p.state = 'idle'; p.blocking = false; break; }
      // shuffle slowly while guarding, face intended direction
      groundMove(world, dt, PLAYER_SPEED * 0.35, PLAYER_TURN_RATE * 0.6);
      if (input.takeDodge() && p.dodgeCd <= 0 && Math.hypot(p.moveX, p.moveZ) > 0.2) {
        p.blocking = false; startDodge(world, p.moveX, p.moveZ);
      }
      break;
    }

    case 'titanfall': {
      groundMove(world, dt, PLAYER_SPEED * 0.9, PLAYER_TURN_RATE);
      break;
    }
  }

  integrateVertical(p, dt);
  clampToField(p);
  world.colliders.resolve(p, p.radius);   // resolve LAST — never end a tick inside a solid
}

function updateAttack(world, input, dt) {
  const p = world.player;
  const node = p.atkNode;
  p.attackT += dt;
  const t = p.attackT;

  // forward lunge
  if (node.move && t >= node.move.t0 && t <= node.move.t1) {
    p.x += fwdX(p.heading) * node.move.speed * dt;
    p.z += fwdZ(p.heading) * node.move.speed * dt;
  }

  // hit windows
  for (let k = 0; k < node.hits.length; k++) {
    const spec = node.hits[k];
    if (t >= spec.t0 && !p._winOpen[k]) { p._winOpen[k] = true; _tok[k] = ++world.nextToken; }
    if (t >= spec.t0 && t <= spec.t1) {
      resolveHit(world, spec, p.x, p.z, p.heading, _tok[k]);
    }
  }

  // finisher shockwave
  if (node.shockwave && !p._shockDone && t >= node.shockwave.t) {
    p._shockDone = true;
    const sx = p.x + fwdX(p.heading) * 1.5, sz = p.z + fwdZ(p.heading) * 1.5;
    spawnShockwave(world, sx, sz, node.shockwave.dmg, node.shockwave.knock, node.shockwave.range, 30, TEAM_PLAYER, 0);
    world.shake = Math.min(SHAKE_MAX, world.shake + 0.6);
  }

  // buffer follow-ups
  if (input.takeN()) { p.buffer = 'N'; p.bufferT = 0.28; }
  if (input.takeC()) { p.buffer = 'C'; p.bufferT = 0.28; }
  if (p.buffer) p.bufferT -= dt;

  // cancel into branch within window — tested BEFORE expiring the buffer so a
  // press whose window opens on its final valid tick still connects.
  if (p.buffer && t >= node.cancel[0] && t <= node.cancel[1]) {
    const aim = (Math.hypot(p.moveX, p.moveZ) > 0.2) ? Math.atan2(p.moveX, p.moveZ) : null;
    if (p.buffer === 'N' && node.next) { startAttack(world, NODES[node.next], aim); return; }
    if (p.buffer === 'C' && node.charge) { startAttack(world, NODES[node.charge], aim); return; }
  }
  if (p.buffer && p.bufferT <= 0) p.buffer = null;

  // dodge-cancel
  if (input.takeDodge() && p.dodgeCd <= 0 && t > 0.08) {
    if (Math.hypot(p.moveX, p.moveZ) > 0.2) startDodge(world, p.moveX, p.moveZ);
    else startDodge(world, fwdX(p.heading), fwdZ(p.heading));
    return;
  }

  if (t >= node.dur) {
    p.atkNode = null;
    p.state = Math.hypot(p.moveX, p.moveZ) > 0.1 ? 'run' : 'idle';
  }
}

function updateDodge(world, input, dt) {
  const p = world.player;
  p.dodgeT += dt;
  const t = p.dodgeT / DODGE_TIME;
  const speed = DODGE_SPEED * (1 - easeInCubic(clamp01(t)));
  p.x += p.dodgeDirX * speed * dt;
  p.z += p.dodgeDirZ * speed * dt;
  // i-frames window
  if (p.dodgeT >= DODGE_IFRAME_START && p.dodgeT <= DODGE_IFRAME_END) {
    p.invuln = Math.max(p.invuln, 0.05);
  }
  if (world.rng() < 0.4) world._dust = true;
  // attack-cancel the tail of a roll (aerial roll → aerial attack)
  if (p.dodgeT > DODGE_TIME * 0.55) {
    if (input.takeN()) { startAttack(world, p.y > 0.05 ? NODES.JUMP : NODES[START_NORMAL], null); return; }
    if (input.takeC()) { startAttack(world, p.y > 0.05 ? NODES.JUMP : NODES[START_CHARGE], null); return; }
  }
  if (p.dodgeT >= DODGE_TIME) {
    p.state = Math.hypot(p.moveX, p.moveZ) > 0.1 ? 'run' : 'idle';
  }
}

function integrateVertical(p, dt) {
  if (p.y > 0 || p.vy > 0) {
    p.vy -= GRAVITY * dt;
    p.y += p.vy * dt;
    if (p.y <= 0) { p.y = 0; p.vy = 0; }
  }
}

function clampToField(p) {
  const lim = MAP_HALF - 3;
  if (p.x < -lim) p.x = -lim; else if (p.x > lim) p.x = lim;
  if (p.z < -lim) p.z = -lim; else if (p.z > lim) p.z = lim;
}

/**
 * Enemy → Benjen damage. Honours i-frames, blocking mitigation and warlord
 * guard-breaks. Called from the AI when an enemy attack connects.
 */
export function damagePlayer(world, dmg, srcX, srcZ, guardBreak) {
  const p = world.player;
  if (p.state === 'dead' || p.invuln > 0 || p.state === 'titanfall') return;

  if (p.blocking) {
    const toSrc = headingTo(p.x, p.z, srcX, srcZ);
    if (Math.abs(angleDelta(p.heading, toSrc)) <= BLOCK_ARC * 0.5) {
      if (guardBreak) {
        // warlord charge shatters the guard
        p.blocking = false;
        p.state = 'hurt';
        p.guardBroken = GUARDBREAK_STUN;
        dmg *= 0.5;
        world.spawnDmgNum(p.x, 2.4, p.z, 0, 3 /* GUARD BROKEN */);
      } else {
        dmg *= BLOCK_CHIP;
        world.spawnSpark(p.x, 1.4, p.z, 0, 1, 0, 0.2, 1.6, 2 /* block spark */);
      }
    }
  }

  p.hp -= dmg;
  addTitan(world, dmg * TITAN_GAIN_TAKE);
  world.shake = Math.min(SHAKE_MAX, world.shake + 0.12 + dmg * 0.003);
  p.invuln = Math.max(p.invuln, 0.28);   // brief mercy window to avoid stun-lock
  world.events.emit(EV.PLAYER_HURT, { damage: dmg });

  if (p.hp <= 0) {
    p.hp = 0;
    p.state = 'dead';
    p.deaths++;
  }
}
